import { Text } from '@mantine/core';
import type { CSSProperties } from 'react';

export type ScoreSize = 'sm' | 'md' | 'lg' | 'xl';

interface Props {
  score: number | null | undefined;
  size?: ScoreSize;
  killed?: boolean;
}

function colorFor(score: number | null | undefined, killed: boolean): {
  bg: string;
  fg: string;
} {
  if (killed || score === 0) return { bg: '#fee2e2', fg: '#991b1b' };
  if (score === null || score === undefined) return { bg: '#f1f5f9', fg: '#64748b' };
  if (score >= 0.85) return { bg: '#dcfce7', fg: '#166534' };
  if (score >= 0.75) return { bg: '#d1fae5', fg: '#065f46' };
  if (score >= 0.5) return { bg: '#fef3c7', fg: '#92400e' };
  if (score > 0) return { bg: '#ffedd5', fg: '#9a3412' };
  return { bg: '#f1f5f9', fg: '#64748b' };
}

const SIZE_STYLES: Record<ScoreSize, CSSProperties> = {
  sm: { width: 44, height: 44, fontSize: '0.875rem' },
  md: { width: 56, height: 56, fontSize: '1.125rem' },
  lg: { width: 80, height: 80, fontSize: '1.5rem' },
  xl: { width: 112, height: 112, fontSize: '2.25rem' },
};

export function ScoreBadge({ score, size = 'md', killed }: Props): JSX.Element {
  const colors = colorFor(score, killed ?? false);
  const sizeStyle = SIZE_STYLES[size];
  // Top-Treffer ab 95% → GOLDENER Kreis (Betongold!) statt grün, mit Glanz-Glow.
  const isGold = !killed && typeof score === 'number' && score >= 0.95;
  const label = killed
    ? '✕'
    : score === null || score === undefined
      ? '—'
      : `${Math.round(score * 100)}%`;

  return (
    <div
      style={{
        ...sizeStyle,
        borderRadius: '50%',
        background: isGold
          ? 'linear-gradient(135deg, #fef08a 0%, #fbbf24 45%, #d97706 100%)'
          : colors.bg,
        color: isGold ? '#7c2d12' : colors.fg,
        boxShadow: isGold
          ? '0 0 0 2px rgba(253,224,71,0.6), 0 4px 16px rgba(217,119,6,0.55)'
          : undefined,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        letterSpacing: '-0.02em',
        fontVariantNumeric: 'tabular-nums',
        flexShrink: 0,
      }}
    >
      <Text component="span" fw={700} style={{ fontSize: 'inherit' }}>
        {label}
      </Text>
    </div>
  );
}
